import {
	Component,
	ComponentFactoryResolver,
	Input,
	OnInit,
	ViewChild
} from '@angular/core';
import {
	AbstractModal,
	BroadcastService,
	ModalButtonType,
	ToasterType
} from '@cv-invision/core';
import { catchError, takeUntil } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { OutgoingEventsHybridService } from '../service/outgoing-events-hybrid.service';
import { OutgoingEventsHybridTabsComponent } from './outgoing-events-hybrid-tabs/outgoing-events-hybrid-tabs.component';
import {
	HybridDataModel,
	HybridTabConfig
} from '../../generic-integrations/models/outgoing-events.model';

@Component({
	selector: 'cv-invision-edit-modal',
	templateUrl: './edit-modal.component.html',
	styleUrls: ['./edit-modal.component.scss']
})
export class EditModalComponent extends AbstractModal implements OnInit {
	@Input() isEditing: boolean;
	@Input() modelData: any;
	@Input() label: string;

	public integrationName: string;
	public saving = false;
	public hybridConfig: HybridTabConfig;

	private atComponentDestroy: Subject<any> = new Subject<any>();
	private originalPayload: string;

	@ViewChild('tabsComponent') tabsComponent: OutgoingEventsHybridTabsComponent;

	constructor(
		private broadcastService: BroadcastService,
		private service: OutgoingEventsHybridService,
		protected componentFactoryResolver: ComponentFactoryResolver
	) {
		super();
	}

	ngOnInit(): void {
		this.integrationName = this.data.integrationName;

		this.buttons.push({
			name: 'close',
			text: 'Close',
			class: 'btn-sm',
			type: ModalButtonType.ACTION,
			action: this.onCancel.bind(this)
		});

		this.buttons.push({
			name: 'save',
			text: 'Save',
			class: 'btn-sm cv-btn-blue',
			type: ModalButtonType.ACTION,
			disabled: () =>
				!this.tabsComponent?.isFormCompletelyValid() ||
				this.saving ||
				!this.hasChanges(),
			action: () => this.onSave(),
			loading: () => this.saving,
			loadingText: 'Saving'
		});

		this.loadData();
	}

	private loadData(): void {
		this.service
			.getIntegrationOutgoingEvents(this.integrationName)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((result) => {
				const data = result.data;

				const cmpRelation = Object.entries(result.cmpRelation).map(
					([key, label]) => ({
						label: label as string,
						value: key
					})
				);

				const hybridData = new HybridDataModel({
					product: data.product || [],
					productHybrid: data.productHybrid || [],
					productNonHybrid: data.productNonHybrid || [],
					cmpRelation: data.cmpRelation || []
				});

				const optionsPostIf = ['Always', 'Open', 'Closed'].map((opt) => ({
					label: opt,
					value: opt
				}));

				this.hybridConfig = new HybridTabConfig(hybridData, cmpRelation, optionsPostIf);
				this.originalPayload = JSON.stringify(hybridData);
			});
	}

	private hasChanges(): boolean {
		const currentPayload = JSON.stringify(this.tabsComponent?.getCurrentPayload());
		return currentPayload !== this.originalPayload;
	}

	onSave(): void {
		this.saving = true;
		const payload = this.tabsComponent.getCurrentPayload();

		const request$ = this.isEditing
			? this.service.updateOutgoingEvents(this.integrationName, payload)
			: this.service.createOutgoingEvents(this.integrationName, payload);

		request$
			.pipe(
				takeUntil(this.atComponentDestroy),
				catchError((err) => {
					this.errorMsgFunction('Failed to save configuration.');
					this.saving = false;
					return of(null);
				})
			)
			.subscribe((response) => {
				if (response) {
					this.successHandleFunction();
					this.saving = false;
					this.originalPayload = JSON.stringify(payload);
				}
			});
	}

	successHandleFunction(): void {
		const msg = `The Outgoing Event Was ${this.isEditing ? 'Updated' : 'Created'} Successfully`;
		this.broadcastService.broadcast({
			name: ToasterType.SUCCESS,
			event: { message: msg }
		});
	}

	errorMsgFunction(error: string): void {
		this.broadcastService.broadcast({
			name: ToasterType.ERROR,
			event: { message: error }
		});
	}

	onDismiss(): void {
		super.onCancel();
	}
}
